/* eslint-disable @typescript-eslint/no-explicit-any */

import { isValidElement } from "react";

/** Gets a value from an object using dot notation path */
const getValueByPath = (obj: any, path: string): any => {
  const normalizedPath = path.replace(/\[(\d+)\]/g, ".$1");
  const parts = normalizedPath.split(".");
  return parts.reduce(
    (value: any, key: string) =>
      value === null || value === undefined ? undefined : value[key],
    obj
  );
};

/** Safely evaluates a mathematical expression and returns the result as a string */
const evaluateMathExpression = (expression: string, values: any[]): string => {
  try {
    const evalExpression = values.join(" ");
    const result = new Function(`return ${evalExpression}`)();
    return String(result);
  } catch (error) {
    console.warn(
      `Error evaluating mathematical expression: ${expression}`,
      error
    );
    return "0";
  }
};

/** Splits an expression into parts and resolves app state references */
const resolveExpressionParts = (expression: string, appState: any): any[] => {
  const parts = expression.split(/\s*([-+*/])\s*/);
  return parts.map((part) => {
    if (/[-+*/]/.test(part)) return part;
    const trimmedPart = part.trim();
    // Handle empty parts from invalid expressions
    if (trimmedPart === "") return 0;
    const stateValue = getValueByPath(appState, trimmedPart);
    return stateValue !== undefined ? stateValue : part;
  });
};

/** Processes string interpolations, including mathematical operations */
const processStringInterpolation = (value: string, appState: any): string => {
  const interpolationRegex = /{{([^}]+)}}/g;
  let match: RegExpExecArray | null;
  let hydrated = value;

  while ((match = interpolationRegex.exec(value)) !== null) {
    const [fullMatch, expression] = match;
    const trimmedExpression = expression.trim();

    if (/[-+*/]/.test(trimmedExpression)) {
      const values = resolveExpressionParts(trimmedExpression, appState);
      const result = evaluateMathExpression(trimmedExpression, values);
      hydrated = hydrated.replace(fullMatch, result);
    } else {
      const stateValue = getValueByPath(appState, trimmedExpression);
      if (stateValue !== undefined) {
        const escapedValue = String(stateValue).replace(/\$/g, "$$$$");
        hydrated = hydrated.replace(fullMatch, escapedValue);
      }
    }
  }
  return hydrated;
};

/**
 * Hydrates a component state object by replacing string interpolations with values from the app state.
 * Supports nested objects, arrays, and mathematical operations in interpolations.
 *
 * @example
 * const componentState = {
 *   greeting: "Hello {{user.name}}!",
 *   total: "{{price + tax}}",
 *   items: ["{{items[0].name}}", "{{count * 2}}"]
 * };
 * const appState = {
 *   user: { name: "John" },
 *   price: 100,
 *   tax: 20,
 *   items: [{ name: "Item 1" }],
 *   count: 5
 * };
 *
 * // Returns:
 * // {
 * //   greeting: "Hello John!",
 * //   total: "120",
 * //   items: ["Item 1", "10"]
 * // }
 */
export function hydrateComponentStateWithAppState<
  ComponentState = any,
  AppState extends Record<any, any> = any,
>(componentState: ComponentState, appState: AppState): ComponentState {
  if (!componentState || typeof componentState !== "object") {
    return componentState;
  }

  const hydrateValue = (
    value: any,
    key?: string | number,
    parent?: any
  ): any => {
    if (typeof value === "string") {
      const hydrated = processStringInterpolation(value, appState);
      if (parent && key !== undefined) parent[key] = hydrated;
      return hydrated;
    }
    if (Array.isArray(value)) {
      value.forEach((item, idx) => hydrateValue(item, idx, value));
      return value;
    }
    if (isValidElement(value)) {
      return value;
    }

    if (
      value &&
      typeof value === "object" &&
      Object.prototype.toString.call(value) === "[object Object]" &&
      Object.getPrototypeOf(value) === Object.prototype
    ) {
      Object.keys(value).forEach((k) => hydrateValue(value[k], k, value));
      return value;
    }
    return value;
  };

  if (Array.isArray(componentState)) {
    componentState.forEach((item, idx) =>
      hydrateValue(item, idx, componentState)
    );
    return componentState;
  }

  Object.keys(componentState as Record<string, any>).forEach((key) => {
    hydrateValue(
      (componentState as Record<string, any>)[key],
      key,
      componentState
    );
  });

  return componentState;
}
